import React, { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Create } from 'src/Images/icons/common/create.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as Delete } from 'src/Images/icons/common/delete.svg';
import {
  createDimension,
  getUnitOfMeasurements,
} from 'src/redux/Actions/VehicleTracking';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { useNavigate, useLocation } from 'react-router-dom';
import Breadcrumb from 'src/components/Breadcrumb';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import usePageTitle from 'src/utils/SetPageTitle';

export default function CreateDimension() {
  usePageTitle('Cloud VT • Create ');
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const authReducer = useSelector((state) => state.AuthReducer);
  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const unitOfMeasurements = vehicleTrackingReducer?.unitOfMeasurements;
  const [formData, setFormData] = useState({
    name: null,
    description: null,
    dataType: null,
    choiceOptions: [],
    unitOfMeasurement: null,
  });

  const unitOfMeasurementOptions = unitOfMeasurements?.map((unitObj) => ({
    label: unitObj.name,
    value: unitObj.id,
  }));

  const [currentOption, setCurrentOption] = useState('');

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    if (name === 'dataType') {
      setFormData((prev) => ({
        ...prev,
        dataType: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value ?? '',
      }));
    }
  };

  const handleSelectChange = (selectedOption, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleOptionChange = (e) => {
    setCurrentOption(e.target.value);
  };

  const addOption = () => {
    if (currentOption.trim()) {
      setFormData((prevData) => ({
        ...prevData,
        choiceOptions: [...prevData.choiceOptions, currentOption.trim()],
      }));
      setCurrentOption('');
    }
  };

  const removeOption = (indexToRemove) => {
    setFormData((prevData) => {
      const newOptions = [...prevData.choiceOptions];
      newOptions.splice(indexToRemove, 1);
      return {
        ...prevData,
        choiceOptions: newOptions,
      };
    });
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const createData = {
      tenant_organisation_id: tenantOrganisationId,
      name: formData.name,
      data_type: formData.dataType,
      description: formData.description,
      unit_measurement_id: formData.unitOfMeasurement,
      available_values: formData.choiceOptions,
    };
    dispatch(createDimension(createData)).then(() => {
      navigate('/dimensions/');
    });
  };

  useSetBreadcrumbData(location.pathname, 'Create');

  return (
    <div className="page-layout setting-container div-flex-column">
      <div className="div-space-between div-flex-column border-bottom">
        <div className="div-flex-row cg-5 left-padding padding-bottom-8">
          <Breadcrumb />
        </div>
        <div className="div-space-between left-padding">
          <span className="text-xxl text-bold">Create Dimension</span>
        </div>
      </div>
      <div className="div-flex-column div-space-between create-dimension-container setting-container">
        <div className="div-flex-column rg-5">
          <span className="text-sm text-grey">
            Add measurement fields to describe the product dimension.
          </span>
          <div className="div-flex-column rg-20">
            <Row className="g-2">
              <Col xxl={4} xl={4} lg={4}>
                <span className="text-sm text-semi-bold">Name</span>
                <input
                  className="form-input-section"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                />
              </Col>
              <Col xxl={4} xl={4} lg={4}>
                <span className="text-sm text-semi-bold">
                  Unit of measurement
                </span>
                <div className="input-wrapper">
                  <CustomMultiSelect
                    name="productId"
                    isMulti={false}
                    className="multi-select-input-section"
                    onChange={(selectedOption) =>
                      handleSelectChange(selectedOption, 'unitOfMeasurement')
                    }
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change') {
                        dispatch(
                          getUnitOfMeasurements(
                            tenantOrganisationId,
                            inputValue,
                          ),
                        );
                      }
                    }}
                    options={unitOfMeasurementOptions}
                    classNamePrefix="react-select"
                    placeholder="Search unit of measurement"
                    value={unitOfMeasurementOptions?.find(
                      (option) => option.value === formData.unitOfMeasurement,
                    )}
                    filterOption={() => true}
                    onFocus={() =>
                      dispatch(getUnitOfMeasurements(tenantOrganisationId, ''))
                    }
                  />
                  <span className="form-input-section-icon">
                    <Search className="icon-size-sm" color="#9F9F9F" />
                  </span>
                </div>
              </Col>
              <Col xxl={12} xl={12} lg={12}>
                <span className="text-sm text-semi-bold">Description</span>
                <input
                  className="form-input-section"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row className="g-3">
              <Col xxl={3} xl={3} lg={3}>
                <div
                  className="div-flex-column border border-radius-4 grn-modal-header rg-5 cursor-pointer"
                  onClick={() =>
                    handleChange({
                      target: { name: 'dataType', value: '1', type: 'radio' },
                    })
                  }
                  role="radio"
                  aria-checked={formData.dataType === '1'}
                  tabIndex={0}
                >
                  <div className="div-flex-row cg-5 div-align-center">
                    <input
                      type="radio"
                      name="dataType"
                      value="1"
                      checked={formData.dataType === '1'}
                      onChange={handleChange}
                      className="cursor-pointer"
                    />
                    <span className="text-sm text-semi-bold">Integer</span>
                  </div>
                  <span className="text-sm text-grey">
                    Use for number-based integer values.
                  </span>
                </div>
              </Col>
              <Col xxl={3} xl={3} lg={3}>
                <div
                  className="div-flex-column border border-radius-4 grn-modal-header rg-5 cursor-pointer"
                  onClick={() =>
                    handleChange({
                      target: { name: 'dataType', value: '2', type: 'radio' },
                    })
                  }
                  role="radio"
                  aria-checked={formData.dataType === '2'}
                  tabIndex={0}
                >
                  <div className="div-flex-row cg-5 div-align-center">
                    <input
                      type="radio"
                      name="dataType"
                      value="2"
                      checked={formData.dataType === '2'}
                      onChange={handleChange}
                      className="cursor-pointer"
                    />
                    <span className="text-sm text-semi-bold">Decimal</span>
                  </div>
                  <span className="text-sm text-grey">
                    Use for number-based decimal values.
                  </span>
                </div>
              </Col>
              <Col xxl={3} xl={3} lg={3}>
                <div
                  className="div-flex-column border border-radius-4 grn-modal-header rg-5 cursor-pointer"
                  onClick={() =>
                    handleChange({
                      target: { name: 'dataType', value: '3', type: 'radio' },
                    })
                  }
                  role="radio"
                  aria-checked={formData.dataType === '3'}
                  tabIndex={0}
                >
                  <div className="div-flex-row cg-5 div-align-center">
                    <input
                      type="radio"
                      name="dataType"
                      value="3"
                      checked={formData.dataType === '3'}
                      onChange={handleChange}
                      className="cursor-pointer"
                    />
                    <span className="text-sm text-semi-bold">String</span>
                  </div>
                  <span className="text-sm text-grey">
                    Use for text-based values.
                  </span>
                </div>
              </Col>
              <Col xxl={3} xl={3} lg={3}>
                <div
                  className="div-flex-column border border-radius-4 grn-modal-header rg-5 cursor-pointer"
                  onClick={() =>
                    handleChange({
                      target: { name: 'dataType', value: '5', type: 'radio' },
                    })
                  }
                  role="radio"
                  aria-checked={formData.dataType === '5'}
                  tabIndex={0}
                >
                  <div className="div-flex-row cg-5 div-align-center">
                    <input
                      type="radio"
                      name="dataType"
                      value="5"
                      checked={formData.dataType === '5'}
                      onChange={handleChange}
                      className="cursor-pointer"
                    />
                    <span className="text-sm text-semi-bold">Boolean</span>
                  </div>
                  <span className="text-sm text-grey">
                    Use for Yes/No values.
                  </span>
                </div>
              </Col>
              <Col xxl={3} xl={3} lg={3}>
                <div
                  className="div-flex-column border border-radius-4 grn-modal-header rg-5 cursor-pointer"
                  onClick={() =>
                    handleChange({
                      target: { name: 'dataType', value: '4', type: 'radio' },
                    })
                  }
                  role="radio"
                  aria-checked={formData.dataType === '4'}
                  tabIndex={0}
                >
                  <div className="div-flex-row cg-5 div-align-center">
                    <input
                      type="radio"
                      name="dataType"
                      value="4"
                      checked={formData.dataType === '4'}
                      onChange={handleChange}
                      className="cursor-pointer"
                    />
                    <span className="text-sm text-semi-bold">Choice</span>
                  </div>
                  <span className="text-sm text-grey">
                    Use for predefined selectable values.
                  </span>
                  {formData.dataType === '4' && (
                    <div className="div-flex-column rg-10">
                      <div className="div-flex-row cg-10">
                        <input
                          className="form-input-section-grey"
                          placeholder="Enter option value"
                          value={currentOption}
                          onChange={handleOptionChange}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addOption();
                            }
                          }}
                        />
                        <button
                          aria-label="button"
                          className="create-btn-outlined-grey"
                          onClick={addOption}
                        >
                          <Create className="icon-size-sm" />
                        </button>
                      </div>
                      {formData.choiceOptions.length > 0 && (
                        <div className="div-flex-column rg-5">
                          {formData.choiceOptions.map((option, index) => (
                            <div
                              className="div-flex-row cg-10 div-align-center"
                              key={option}
                            >
                              <span className="text-sm form-input-section text-nowrap text-grey">
                                {option}
                              </span>
                              <button
                                aria-label="button"
                                className="create-btn-outlined"
                                onClick={() => removeOption(index)}
                              >
                                <Delete className="icon-size-sm" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </div>
        <div className="div-flex-row div-flex-end">
          <button
            className="btn-black"
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
